<template>
	<div>
		<!-- Headline -->
		<div slot="headline" class="fs5 lh125 fw7 c1 mb2">
			{{ $t("exclusions") }}
		</div>

		<j-card class="bg5">
			<j-list :list="computedList" v-if="computedList.length > 0">
				<div class="flex xs-12 p3" slot="list" slot-scope="{ item }">
					<div class="flex wrap xs-10 xs-left xs-middle fw7 lh125">
						<div class="xs-12 lh125 c1">{{ item.label }}</div>
						<div class="xs-12 fw4 c1 lh125">{{ item.remark }}</div>
					</div>
					<div class="flex xs-2 xs-right xs-middle lh125">
						<div v-if="hasActions">
							<crm-icon
								v-can="'writeQuotes'"
								name="delete"
								class="cu2"
								color="cError"
								size="small"
								@click.native="removeExclusion(item.id)"
							/>
						</div>
					</div>
				</div>
			</j-list>

			<!-- Add/update button -->
			<div class="xs-12 p3" v-if="hasActions">
				<div class="fs3 lh125 c1 mb2" v-if="computedList.length === 0">
					{{ $t("exclusion_is_empty") }}
				</div>
				<j-button
					v-can="'writeQuotes'"
					size="xsmall"
					@click="$modal.show('addExclusion')"
				>
					{{ $t("add_exclusion") }}
				</j-button>
			</div>

			<!-- Empty without actions -->
			<div
				v-if="computedList.length === 0 && !hasActions"
				class="fs3 c1 ta2 p2"
			>
				{{ $t("errors.no_data") }}
			</div>
		</j-card>

		<!-- Modal for adding loading -->
		<core-modal name="addExclusion" :headline="$t('add_exclusion')">
			<exclusions-create
				:caseId="caseId"
				:proposalId="proposalId"
				:offerId="offerId"
				:productId="productId"
				@created="afterExclusionCreated"
				@cancelled="$modal.hide('addExclusion')"
			/>
		</core-modal>
	</div>
</template>

<script>
import JCard from "@/components/JCard"
import JList from "@/components/JList"
import JButton from "@/components/JButton"
import CrmIcon from "@/components/CrmIcon"
import ExclusionsCreate from "./ExclusionsCreate"
import CoreModal from "@/modules/core/components/CoreModal"
import get from "lodash.get"
import api from "@/api/gql.js"

export default {
	components: {
		JCard,
		JList,
		JButton,
		CrmIcon,
		ExclusionsCreate,
		CoreModal
	},

	props: {
		hasActions: {
			type: Boolean,
			required: false,
			default: false
		}
	},

	data() {
		return {
			cases: [],
			list: []
		}
	},

	computed: {
		offerId() {
			return this.$store.state.caseManagement.offerId
		},
		proposalId() {
			return this.$store.state.caseManagement.proposalId
		},
		caseId() {
			return this.$store.state.caseManagement.caseId
		},
		proposal() {
			return this.$store.state.caseManagement.proposals.filter(p => {
				return p.id === this.proposalId
			})[0]
		},
		offer() {
			return (
				this.proposal &&
				this.proposal.basket.filter(b => {
					return b.id === this.offerId
				})[0]
			)
		},
		productId() {
			return (this.offer && this.offer?.product?.productId) || {}
		},
		exclusions() {
			return (this.offer && this.offer.exclusions) || []
		},
		computedList() {
			return this.exclusions.map(item => ({
				id: item.id,
				label: `${get(item, "codeName.code")} - ${get(item, "codeName.name")}`,
				remark: item.remark
			}))
		}
	},

	async mounted() {
		await this.fetchExclusions()
	},

	methods: {
		async fetchExclusions() {
			this.$store.dispatch("caseManagement/fetchProposals", {
				caseId: this.$store.state.caseManagement.caseId
			})
		},
		async removeExclusion(id) {
			try {
				await this.removeExclusionFromProposalOffer(id)
				await this.fetchExclusions()
			} catch (error) {
				console.log(error)
			}

			// Emit
			this.$emit("deleted")
		},

		async afterExclusionCreated() {
			this.$modal.hide("addExclusion")
			await this.fetchExclusions()
			this.$emit("created")
		},

		getCases() {
			return new Promise(async (resolve, reject) => {
				const query = `query cases ($caseWhere: caseWhere, $basketWhere: offerWhere, $proposalWhere: proposalWhere) {
					cases(where: $caseWhere) {
						list {
							proposals (where: $proposalWhere) {
								basket (where: $basketWhere) {
									product {
										productId {
											plan
											type
											version
										}
									}
									exclusions {
										id
										codeName {
											code
											name
										}
										remark
									}
								}
							}
						}
					}
				}`

				const variables = {
					caseWhere: {
						id: this.caseId
					},
					proposalWhere: {
						id: this.proposalId
					},
					basketWhere: {
						id: this.offerId
					}
				}
				await this.APIRequest("cases", query, variables, resolve, reject)
			})
		},

		removeExclusionFromProposalOffer(id) {
			return new Promise(async (resolve, reject) => {
				const query = `mutation removeExclusionFromProposalOffer (
					$caseId: String!,
					$proposalId: String!,
					$offerId: String!,
					$exclusionId: String!
				) {
					removeExclusionFromProposalOffer (
						caseId: $caseId,
						proposalId: $proposalId,
						offerId: $offerId,
						exclusionId: $exclusionId
					) {
						errors
						status
					}
				}`

				const variables = {
					caseId: this.caseId,
					proposalId: this.proposalId,
					offerId: this.offerId,
					exclusionId: id
				}
				await this.APIRequest(
					"removeExclusionFromProposalOffer",
					query,
					variables,
					resolve,
					reject
				)
			})
		},

		async APIRequest(type, query, variables, resolve, reject) {
			try {
				const { data, errors } = await api({
					query,
					variables
				})

				if (errors) {
					reject(errors)
				} else if (data.errors) {
					reject(data.errors)
				} else {
					resolve(data[type].createdStatus || data[type].list || data[type])
				}
			} catch (error) {
				reject(error)
			}
		}
	}
}
</script>
